import { Fragment, useEffect, useState, useMemo } from "react";

import * as StateLib from "@/Utils/StateLib";
import cx from "classnames";
import { Alert, Button, Divider, Flex, isNullish, Text, Spinner } from "@theorgchartdev/storybook";
import { ConflictField } from "./ConflictField";
import {
  ConflictedRecordExists,
  GetChangeFieldRevertInfo,
  GetChangeFieldReverts,
  Resolve,
  Revert,
  SendUpdate
} from "./ConflictResolutionModal.utils";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import useResizable from "@/hooks/useResizable";
import { useMinimumLoadingTime } from "@/hooks/useMinimumLoadingTime";
import { CardWrapper } from "@/appComponents/Blocks/Cards/CardWrapper/CardWrapper";
import { handleNavigation } from "@/appComponents/Navigation/RightSidebar/Planning/Activity/Activity";
import { useTopic } from "@/hooks/useTopic";
import { appstr } from "@coreapp/utils/appstr";
import { appstrp } from "@coreapp/utils/appstrp";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { GetObjClassForOverride } from "@coreapp/utils/GetObjClassForOverride";
import { enumObjectTypes } from "@coreapp/constants";
import { ConflictRecordChip } from "@/appComponents/Blocks/Composites/ConflictRecordChip/ConflictRecordChip";
import { getContainerHeight, getContainerWidth } from "@/Utils/getContainerSize";

export const ConflictResolutionModal = () => {
  const { conflictContextValue, planManager } = usePlanManagerContext();
  const planMgr = planManager.state;
  const { showConflictModal, setShowConflictModal, conflictOverride, setConflictOverride } =
    conflictContextValue;

  const [hasLoaded, setLoaded] = useState(false);
  const [isLoadingConflict, setIsLoadingConflict] = useState(false);
  const [dbObj, setDbObj] = useState(null);
  const [planSupervisor, setPlanSupervisor] = useState(null);
  const [sourceSupervisor, setSourceSupervisor] = useState(null);
  const [changeFieldReverts, setChangeFieldReverts] = useState({});
  const [pickListSelections, setPickListSelections] = useState({});

  const [currentConflictIndex, setCurrentConflictIndex] = useState(0);

  const { startLoading, finishLoading } = useMinimumLoadingTime(500);

  const allConflictOverrides = planMgr?.fTrackList?.GetFilteredObjList() || [];

  const onClose = () => {
    setLoaded(false);
    setConflictOverride(null);
    setShowConflictModal(false);
    setChangeFieldReverts({});
    setCurrentConflictIndex(0);
  };

  useEffect(() => {
    if (!showConflictModal || allConflictOverrides.length === 0) return;

    if (conflictOverride) {
      const index = allConflictOverrides.findIndex(
        o => o.SmartGetFieldValue("RefID") === conflictOverride.SmartGetFieldValue("RefID")
      );
      if (index >= 0) {
        setCurrentConflictIndex(index);
      } else {
        setCurrentConflictIndex(0);
      }
    } else {
      setCurrentConflictIndex(0);
    }
  }, [showConflictModal, conflictOverride, allConflictOverrides]);

  const currentOverride = allConflictOverrides[currentConflictIndex] || null;

  const initialWidth = 600;
  const initialHeight = 600;
  const minWidth = 400;
  const minHeight = 400;

  const { size, handleMouseDown } = useResizable(initialWidth, initialHeight);
  const initOffset = {
    x: (getContainerWidth() - initialWidth) / 2,
    y: (getContainerHeight() - initialHeight) / 2
  };

  useTopic("ModeSwitch", nextUIMode => {
    if (!planMgr) setShowConflictModal(false);
  });

  // Listen for override updates and refresh conflict list
  useTopic("loadedOverrides", () => {
    if (!showConflictModal) return;

    const updatedConflicts = planMgr?.fTrackList?.GetFilteredObjList() || [];

    if (updatedConflicts.length === 0) {
      onClose();
    } else {
      // Reset to first conflict after resolution
      setCurrentConflictIndex(0);
      setConflictOverride(null);
    }
  });

  const hydrateRecord = record => {
    if (isNullish(record)) {
      return null;
    }

    const doc = StateLib.GetDoc();
    const objClass = GetObjClassForOverride(currentOverride, doc);

    const dbObj = new QObjDBObject();
    dbObj.InitObject(objClass);
    objClass.ProcessObject(dbObj, record);

    return dbObj;
  };

  useEffect(() => {
    setPickListSelections({});
    setChangeFieldReverts({});
    if (!showConflictModal || isNullish(currentOverride) || !planMgr) {
      setLoaded(false);
      setIsLoadingConflict(false);
      return;
    }

    const activeChange = currentOverride.lastActiveChange();
    if (isNullish(activeChange)) {
      return;
    }

    setIsLoadingConflict(true);
    startLoading();

    const payload = {
      positionIDs: [],
      recordIDs: [],
      includeConflicts: true
    };

    const recordId = currentOverride.SmartGetFieldValue("RefID");
    const objType = currentOverride.SmartGetFieldValue("objType") ?? enumObjectTypes.holder;
    if (objType === enumObjectTypes.position) {
      payload.positionIDs.push(recordId);
    } else {
      payload.recordIDs.push(recordId);
    }

    planMgr.LoadRecords(payload, r => {
      const cachedObj = planMgr.GetCacheBaseRecord(objType, recordId);
      if (cachedObj) {
        cachedObj.fCloneBase = cachedObj.Clone(true);
        currentOverride.ApplyChangesReadOnly(cachedObj);
      }

      setDbObj(cachedObj);

      const newSourceSupervisor = hydrateRecord(r["sourceSupervisors"][0]);
      setSourceSupervisor(newSourceSupervisor);

      const newPlanSupervisor = hydrateRecord(r["planSupervisors"][0]);
      setPlanSupervisor(newPlanSupervisor);

      finishLoading(() => {
        setIsLoadingConflict(false);
        setLoaded(true);
      });
    });

    const changeFieldReverts = GetChangeFieldReverts(currentOverride);
    setChangeFieldReverts(changeFieldReverts);
  }, [showConflictModal, currentOverride, currentConflictIndex, planMgr]);

  if (!showConflictModal || hasLoaded === false) {
    return;
  }

  const onRevert = () => {
    const shouldRevertAll = Revert(dbObj, currentOverride);
    SendUpdate(currentOverride, shouldRevertAll);
  };

  const onResolve = () => {
    const shouldRevertAll = Resolve(dbObj, currentOverride, changeFieldReverts);
    SendUpdate(currentOverride, shouldRevertAll);
  };

  const handlePrevConflict = () => {
    if (currentConflictIndex > 0) {
      setCurrentConflictIndex(currentConflictIndex - 1);
    }
  };

  const handleNextConflict = () => {
    if (currentConflictIndex < allConflictOverrides.length - 1) {
      setCurrentConflictIndex(currentConflictIndex + 1);
    }
  };

  const recExists = ConflictedRecordExists(currentOverride, dbObj);

  const doc = StateLib.GetDoc();
  if (!doc || !planMgr || !currentOverride) return;

  const objClass = GetObjClassForOverride(currentOverride, doc);
  const supervisorField = objClass?.GetParentIDFieldDef()?.GetFieldName() ?? "";
  const revertInfo = GetChangeFieldRevertInfo({
    field: supervisorField,
    overrideObj: currentOverride,
    planMgr: planMgr,
    dbObj: dbObj,
    planSupervisor: planSupervisor,
    sourceSupervisor: sourceSupervisor,
    changeFieldReverts: changeFieldReverts
  });

  let shouldResolve = recExists && !revertInfo.invalidPlanSuper;
  if (revertInfo.invalidPlanSuper) {
    shouldResolve =
      Object.keys(changeFieldReverts).length === 1 && pickListSelections[supervisorField];
  }

  const commitLabel = shouldResolve ? appstr("Resolve") : appstr("Revert changes");
  const onCommit = shouldResolve ? onResolve : onRevert;

  const footerProps = {
    modifier: "grey",
    spacingClass: "",
    footerActions: {
      customFooterContent: (
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Button
            dataTestId="resolve-plan-conflict-cancel-btn"
            onClick={onClose}
            modifier="tertiary"
            size="medium"
            style={{
              marginLeft: "12px",
              marginRight: "auto",
              position: "absolute",
              left: 0
            }}
          >
            {appstr("Close")}
          </Button>
          {allConflictOverrides.length > 1 && (
            <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
              <Flex gap="8px" alignItems="center">
                <Button
                  dataTestId="planning-changes-conflict-prev-btn"
                  modifier="tertiary"
                  size="small"
                  onClick={handlePrevConflict}
                  disabled={currentConflictIndex === 0}
                  iconProps={{ name: "chevron-left" }}
                />
                <Text size="small" colorModifier="grey">
                  {currentConflictIndex + 1} / {allConflictOverrides.length}
                </Text>
                <Button
                  dataTestId="planning-changes-conflict-next-btn"
                  modifier="tertiary"
                  size="small"
                  onClick={handleNextConflict}
                  disabled={currentConflictIndex === allConflictOverrides.length - 1}
                  iconProps={{ name: "chevron-right" }}
                />
              </Flex>
            </div>
          )}
          <Button
            dataTestId="resolve-plan-conflict-btn"
            onClick={onCommit}
            modifier="primary"
            size="medium"
            style={{ marginRight: 0, marginLeft: "auto" }}
          >
            {commitLabel}
          </Button>
        </div>
      )
    }
  };

  const getHelpText = () => {
    return recExists ? appstr("Choose Source") : appstr("Employee Removed");
  };

  let changedFieldRows = changeFieldReverts;
  if (!recExists) {
    const newObj = {};
    Object.keys(changedFieldRows).some(changeField => {
      newObj[changeField] = changedFieldRows[changeField];
      return true;
    });
    changedFieldRows = newObj;
  }

  return (
    <CardWrapper
      show={showConflictModal}
      prefixId={"conflict-resolution-card"}
      onClose={onClose}
      otherHeaderProps={{
        title: appstr("Resolve conflict"),
        icon: "circle-exclamation",
        iconProps: { color: "error", size: "medium" },
        modifier: "grey",
        labelSize: "large",
        size: "large"
      }}
      otherFooterProps={{ ...footerProps }}
      size={size}
      onMouseDownResize={handleMouseDown}
      initOffset={initOffset}
      minWidth={minWidth}
      minHeight={minHeight}
      otherOverlayProps={{ padding: "16px", labelSize: "large" }}
      showBackgroundOverlay={false}
      customClass={"oc-view-rule-card"}
    >
      <Flex direction={"column"} gap={"18px"} height={"100%"} overflow={"hidden"}>
        {isLoadingConflict ? (
          <Flex
            direction={"column"}
            gap={"18px"}
            alignItems="center"
            justifyContent="center"
            style={{ flex: 1 }}
          >
            <Flex gap={"8px"} alignItems="flex-start">
              <Text size="small" modifier={"bold"} style={{ flexShrink: 0 }}>
                {appstrp("Conflicted record", ":")}
              </Text>
            </Flex>
            <Spinner size="medium" />
          </Flex>
        ) : (
          <>
            <Flex direction={"column"} gap={"18px"}>
              <Alert
                severity={recExists ? "info" : "warning"}
                className={cx("oc-padding-small", "oc-text-small")}
              >
                {getHelpText()}
              </Alert>
              <Flex gap={"8px"} alignItems={"center"}>
                <Text size="small" modifier={"bold"}>
                  {appstrp("Conflicted record", ":")}
                </Text>
                <ConflictRecordChip
                  dbObj={dbObj}
                  override={currentOverride}
                  onClick={() => handleNavigation(currentOverride)}
                  disabled={!recExists}
                />
              </Flex>
            </Flex>
            <div className="oc-conflict-label-bar oc-conflict-resolution-row oc-conflict-resolution-row-content">
              <Text size="small" modifier="bold" colorModifier={"grey"}>
                {appstr("Plan changes")}
              </Text>
              <Text size="small" modifier="bold" colorModifier={"grey"}>
                {appstr("Source data changes")}
              </Text>
            </div>
            <Flex
              direction={"column"}
              gap={"16px"}
              className={cx("oc-list-container", "ocn-list-scrollbar")}
            >
              {Object.keys(changedFieldRows).map(changeField => (
                <Fragment key={changeField}>
                  <ConflictField
                    key={changeField}
                    field={changeField}
                    overrideObj={currentOverride}
                    planMgr={planMgr}
                    dbObj={dbObj}
                    planSupervisor={planSupervisor}
                    setPlanSupervisor={setPlanSupervisor}
                    sourceSupervisor={sourceSupervisor}
                    changeFieldReverts={changeFieldReverts}
                    setChangeFieldReverts={setChangeFieldReverts}
                    isPickListSelected={pickListSelections[changeField] === true}
                    setIsPickListSelected={nextSelected =>
                      setPickListSelections(p => ({ ...p, [changeField]: nextSelected }))
                    }
                    hydrateRecord={hydrateRecord}
                  />
                  <Divider />
                </Fragment>
              ))}
            </Flex>
          </>
        )}
      </Flex>
    </CardWrapper>
  );
};
